import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { 
  ArrowLeft, 
  Factory, 
  Globe, 
  Award, 
  Truck, 
  Building2, 
  Rocket,
  MapPin,
  Calendar,
  ChevronDown,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Handshake,
  Youtube,
  ChevronRight,
  Zap,
  Leaf
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// Données des vidéos YouTube par modèle
const videoData = [
  {
    model: "909 ECR",
    description: "Mini-pelle compacte 0.9T - Idéale pour les espaces restreints",
    color: "from-blue-500 to-cyan-600",
    videos: [
      { id: "eqkY9PppP9U", title: "Présentation 909 ECR", lang: "🇫🇷 Français" },
      { id: "Gn-9NrIByY8", title: "909 ECR avec broyeur végétaux", lang: "🇫🇷 Français" },
    ]
  },
  {
    model: "9018F",
    description: "Mini-pelle 1.8T - Polyvalente et maniable",
    color: "from-green-500 to-emerald-600",
    videos: [
      { id: "8Vg0S9glMjg", title: "Présentation 9018F", lang: "🇫🇷 Français" },
    ]
  },
  {
    model: "9027F",
    description: "Mini-pelle 2.7T - Notre best-seller",
    color: "from-yellow-500 to-orange-600",
    videos: [
      { id: "VJzijl3uetQ", title: "Présentation 9027", lang: "🇬🇧 English" },
      { id: "Wylxd4v1txY", title: "Test terrain 9027", lang: "🇬🇧 English" },
      { id: "MT9EKXdNhw4", title: "Performance 9027", lang: "🇬🇧 English" },
      { id: "csdTCyh9y04", title: "Démonstration 9027", lang: "🇬🇧 English" },
      { id: "zx51cAnb3zQ", title: "9027 en action", lang: "🇬🇧 English" },
      { id: "4Di2ffxq7m8", title: "Capacités 9027", lang: "🇬🇧 English" },
      { id: "W4KuJbxzIns", title: "9027 chantier", lang: "🇬🇧 English" },
      { id: "dC0VHxqhC0E", title: "Test complet 9027", lang: "🇬🇧 English" },
      { id: "mRCrd4ajuEU", title: "9027 polyvalence", lang: "🇬🇧 English" },
      { id: "WtYpmuis6-4", title: "9027 travaux", lang: "🇬🇧 English" },
    ]
  },
  {
    model: "9027 Électrique",
    description: "Mini-pelle 2.7T 100% électrique - Zéro émission",
    color: "from-emerald-400 to-teal-600",
    icon: Zap,
    videos: [
      { id: "z4-I-W-7n14", title: "9027 Électrique - Présentation", lang: "🇬🇧 English" },
      { id: "3z2s8FCGCRc", title: "9027E - Performance", lang: "🇬🇧 English" },
      { id: "_VtXN9cYHBM", title: "9027E - Autonomie", lang: "🇬🇧 English" },
    ]
  },
  {
    model: "9035F",
    description: "Mini-pelle 3.5T - Puissance et précision",
    color: "from-purple-500 to-pink-600",
    videos: [
      { id: "JVc_WhnxzSE", title: "Présentation 9035F", lang: "🇬🇧 English" },
      { id: "TY1oaNVR2YE", title: "9035F en action", lang: "🇬🇧 English" },
    ]
  },
  {
    model: "9057F",
    description: "Mini-pelle 5.7T - Pour les gros travaux",
    color: "from-red-500 to-rose-600",
    videos: [
      { id: "nEPGwPtFHaQ", title: "Présentation 9057F", lang: "🇬🇧 English" },
    ]
  },
  {
    model: "913 FOR",
    description: "Pelle sur pneus forestière - Spéciale environnement",
    color: "from-green-600 to-lime-500",
    icon: Leaf,
    videos: [
      { id: "rqG2v_he_ac", title: "913 FOR - Application forestière", lang: "🇬🇧 English" },
    ]
  },
  {
    model: "915 FCR",
    description: "Pelle forestière compacte - Polyvalente",
    color: "from-amber-500 to-yellow-600",
    videos: [
      { id: "2_rYT7x4O3Y", title: "915 FCR - Présentation", lang: "🇬🇧 English" },
    ]
  },
];

// Données de la frise chronologique LiuGong
const timelineData = [
  {
    year: 1958,
    title: "La Naissance d'un Géant",
    description: "Plus de 500 ingénieurs de Shanghai et de toute la Chine arrivent à Liuzhou pour fonder LiuGong. C'est le début d'une aventure industrielle extraordinaire.",
    icon: Factory,
    color: "from-amber-500 to-orange-600",
    image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600",
    fact: "500+ ingénieurs pionniers"
  },
  {
    year: 1966,
    title: "Première Chargeuse Moderne",
    description: "LiuGong développe la Z435, la première chargeuse sur pneus modernisée de Chine. Elle obtient la certification nationale en 1968.",
    icon: Truck,
    color: "from-blue-500 to-cyan-600",
    image: "https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=600",
    fact: "Modèle Z435 - Une révolution"
  },
  {
    year: 1971,
    title: "Innovation Articulée",
    description: "Lancement de la Z450, la première chargeuse articulée de Chine. Une prouesse technique qui positionne LiuGong comme leader de l'innovation.",
    icon: Award,
    color: "from-green-500 to-emerald-600",
    image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600",
    fact: "Première chargeuse articulée chinoise"
  },
  {
    year: 1976,
    title: "Records de Puissance",
    description: "Développement de la ZL90, la plus grande chargeuse de Chine, et de la ZL50 souterraine. LiuGong repousse les limites du possible.",
    icon: Rocket,
    color: "from-purple-500 to-pink-600",
    image: "https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=600",
    fact: "ZL90 - La plus grande de Chine"
  },
  {
    year: 1987,
    title: "Licence Caterpillar",
    description: "LiuGong obtient la licence de fabrication des chargeuses Cat 966 de Caterpillar. Ce transfert de technologie américaine propulse LiuGong vers les standards internationaux de qualité.",
    icon: Handshake,
    color: "from-yellow-500 to-amber-600",
    image: "https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=600",
    fact: "Technologie Caterpillar 🇺🇸"
  },
  {
    year: 1993,
    title: "Entrée en Bourse",
    description: "LiuGong devient la première entreprise d'équipement de construction cotée à la Bourse de Shenzhen. Un moment historique pour l'industrie chinoise.",
    icon: Building2,
    color: "from-red-500 to-rose-600",
    image: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=600",
    fact: "1ère entreprise BTP cotée en Chine"
  },
  {
    year: 1995,
    title: "Alliance avec ZF",
    description: "Partenariat stratégique avec le géant allemand ZF pour les transmissions. Cette collaboration perdure encore aujourd'hui.",
    icon: Globe,
    color: "from-indigo-500 to-blue-600",
    image: "https://images.unsplash.com/photo-1518611012118-696072aa579a?w=600",
    fact: "Joint-venture Chine-Allemagne"
  },
  {
    year: 2007,
    title: "Conquête de l'Inde",
    description: "Établissement de la division Inde et construction d'une usine à Indore. La première chargeuse sort des chaînes en 2009.",
    icon: MapPin,
    color: "from-orange-500 to-amber-600",
    image: "https://images.unsplash.com/photo-1524492412937-b28074a5d7da?w=600",
    fact: "Première usine hors de Chine"
  },
  {
    year: 2012,
    title: "Expansion Européenne",
    description: "Acquisition de HSW (Pologne) et Dressta. LiuGong s'implante solidement en Europe et diversifie sa gamme de produits.",
    icon: Globe,
    color: "from-teal-500 to-cyan-600",
    image: "https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=600",
    fact: "Acquisition stratégique en Pologne"
  },
  {
    year: 2024,
    title: "Leader Mondial",
    description: "Aujourd'hui, LiuGong est le 10ème plus grand fabricant mondial d'équipements de construction, présent dans plus de 170 pays avec 20+ usines.",
    icon: Award,
    color: "from-yellow-500 to-orange-600",
    image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600",
    fact: "170+ pays • 20+ usines • Top 10 mondial"
  }
];

// Composant pour chaque carte de la timeline
const TimelineCard = ({ item, index, isActive, onClick }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const isLeft = index % 2 === 0;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: isLeft ? -100 : 100 }}
      animate={isInView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className={`flex items-center gap-8 ${isLeft ? 'flex-row' : 'flex-row-reverse'} relative`}
    >
      {/* Carte */}
      <motion.div
        whileHover={{ scale: 1.02, y: -5 }}
        whileTap={{ scale: 0.98 }}
        onClick={onClick}
        className={`w-[calc(50%-40px)] cursor-pointer group ${isLeft ? 'text-right' : 'text-left'}`}
      >
        <div className={`bg-white rounded-2xl shadow-lg overflow-hidden border-2 transition-all duration-300 ${
          isActive ? 'border-yellow-500 shadow-yellow-500/20 shadow-xl' : 'border-transparent hover:border-yellow-300'
        }`}>
          {/* Image */}
          <div className="relative h-40 overflow-hidden">
            <img 
              src={item.image} 
              alt={item.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
            <div className={`absolute inset-0 bg-gradient-to-t ${item.color} opacity-60`} />
            <div className="absolute bottom-3 left-3 right-3">
              <span className="inline-flex items-center gap-2 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-bold text-slate-800">
                <Calendar className="w-4 h-4" />
                {item.year}
              </span>
            </div>
          </div>
          
          {/* Contenu */}
          <div className="p-5">
            <h3 className="text-lg font-bold text-slate-800 mb-2 group-hover:text-yellow-600 transition-colors">
              {item.title}
            </h3>
            <p className="text-slate-600 text-sm line-clamp-2">
              {item.description}
            </p>
            <div className={`mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${item.color} text-white`}>
              <item.icon className="w-3 h-3" />
              {item.fact}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Point central */}
      <motion.div
        initial={{ scale: 0 }}
        animate={isInView ? { scale: 1 } : {}}
        transition={{ duration: 0.4, delay: index * 0.1 + 0.3 }}
        className={`absolute left-1/2 -translate-x-1/2 z-10 w-16 h-16 rounded-full bg-gradient-to-br ${item.color} flex items-center justify-center shadow-lg ${
          isActive ? 'ring-4 ring-yellow-400 ring-offset-2' : ''
        }`}
      >
        <item.icon className="w-7 h-7 text-white" />
      </motion.div>

      {/* Espace pour l'autre côté */}
      <div className="w-[calc(50%-40px)]" />
    </motion.div>
  );
};

// Composant Modal de détail
const DetailModal = ({ item, onClose }) => {
  if (!item) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 50 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 50 }}
        className="bg-white rounded-3xl max-w-2xl w-full overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Image Header */}
        <div className="relative h-64">
          <img 
            src={item.image} 
            alt={item.title}
            className="w-full h-full object-cover"
          />
          <div className={`absolute inset-0 bg-gradient-to-t ${item.color} opacity-70`} />
          <div className="absolute inset-0 flex flex-col justify-end p-8 text-white">
            <motion.span
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-6xl font-black mb-2"
            >
              {item.year}
            </motion.span>
            <motion.h2
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-2xl font-bold"
            >
              {item.title}
            </motion.h2>
          </div>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition"
          >
            ✕
          </button>
        </div>

        {/* Contenu */}
        <div className="p-8">
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-slate-600 text-lg leading-relaxed mb-6"
          >
            {item.description}
          </motion.p>
          
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            className={`inline-flex items-center gap-3 px-5 py-3 rounded-full bg-gradient-to-r ${item.color} text-white font-medium`}
          >
            <item.icon className="w-5 h-5" />
            {item.fact}
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Composant carte vidéo YouTube
const VideoCard = ({ video, modelColor }) => {
  const [isPlaying, setIsPlaying] = useState(false);

  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -5 }}
      className="bg-slate-800 rounded-xl overflow-hidden shadow-lg"
    >
      {!isPlaying ? (
        <div 
          className="relative aspect-video cursor-pointer group"
          onClick={() => setIsPlaying(true)}
        >
          <img 
            src={`https://img.youtube.com/vi/${video.id}/maxresdefault.jpg`}
            alt={video.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`;
            }}
          />
          <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-all flex items-center justify-center">
            <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${modelColor} flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform`}>
              <Play className="w-7 h-7 text-white ml-1" fill="white" />
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
            <p className="text-white font-medium text-sm truncate">{video.title}</p>
            <p className="text-white/70 text-xs">{video.lang}</p>
          </div>
        </div>
      ) : (
        <div className="aspect-video">
          <iframe
            width="100%"
            height="100%"
            src={`https://www.youtube.com/embed/${video.id}?autoplay=1`}
            title={video.title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
      )}
    </motion.div>
  );
};

// Composant section modèle
const ModelSection = ({ model, index }) => {
  const [expanded, setExpanded] = useState(index === 0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const IconComponent = model.icon || Truck;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="mb-6"
    >
      {/* Header du modèle */}
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all ${
          expanded 
            ? `bg-gradient-to-r ${model.color} text-white shadow-lg` 
            : 'bg-slate-800 text-white hover:bg-slate-700'
        }`}
      >
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
          expanded ? 'bg-white/20' : `bg-gradient-to-r ${model.color}`
        }`}>
          <IconComponent className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 text-left">
          <h3 className="text-xl font-bold">{model.model}</h3>
          <p className={`text-sm ${expanded ? 'text-white/80' : 'text-slate-400'}`}>
            {model.description}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            expanded ? 'bg-white/20' : 'bg-slate-700'
          }`}>
            {model.videos.length} vidéo{model.videos.length > 1 ? 's' : ''}
          </span>
          <ChevronRight className={`w-6 h-6 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </div>
      </button>

      {/* Grille de vidéos */}
      {expanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {model.videos.map((video, vidIndex) => (
            <VideoCard key={video.id} video={video} modelColor={model.color} />
          ))}
        </motion.div>
      )}
    </motion.div>
  );
};

// Composant principal
export default function LiuGongHistoryPage() {
  const [activeIndex, setActiveIndex] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

  // Auto-play animation
  useEffect(() => {
    let interval;
    if (isAutoPlaying) {
      let currentIndex = 0;
      interval = setInterval(() => {
        setActiveIndex(currentIndex);
        currentIndex = (currentIndex + 1) % timelineData.length;
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 px-4">
        {/* Animated background */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-yellow-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          <Link to="/portail">
            <Button variant="ghost" className="text-white/70 hover:text-white mb-8">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour au portail
            </Button>
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            {/* Logo LiuGong stylisé */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, type: "spring" }}
              className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl shadow-2xl shadow-yellow-500/30 mb-8"
            >
              <span className="text-4xl font-black text-white">LG</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-4xl md:text-6xl font-black text-white mb-4"
            >
              L'Histoire de{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">
                LiuGong
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-xl text-slate-300 max-w-2xl mx-auto mb-8"
            >
              De 1958 à aujourd'hui, découvrez l'épopée d'un géant chinois devenu leader mondial des équipements de construction
            </motion.p>

            {/* Stats rapides */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-wrap justify-center gap-6 mb-12"
            >
              {[
                { value: '65+', label: 'Années d\'histoire' },
                { value: '170+', label: 'Pays desservis' },
                { value: '20+', label: 'Usines mondiales' },
                { value: 'Top 10', label: 'Mondial' }
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl px-6 py-4 border border-white/20"
                >
                  <div className="text-3xl font-black text-yellow-400">{stat.value}</div>
                  <div className="text-sm text-slate-300">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>

            {/* Boutons d'action */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex justify-center gap-4"
            >
              <Button
                onClick={() => setIsAutoPlaying(!isAutoPlaying)}
                className={`px-6 py-3 rounded-full font-semibold transition-all ${
                  isAutoPlaying 
                    ? 'bg-white text-slate-900 hover:bg-slate-100' 
                    : 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white hover:shadow-lg hover:shadow-yellow-500/30'
                }`}
              >
                {isAutoPlaying ? (
                  <><Pause className="w-5 h-5 mr-2" /> Pause</>
                ) : (
                  <><Play className="w-5 h-5 mr-2" /> Lecture automatique</>
                )}
              </Button>
            </motion.div>

            {/* Scroll indicator */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="mt-16"
            >
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="text-white/50"
              >
                <ChevronDown className="w-8 h-8 mx-auto" />
                <span className="text-sm">Défilez pour explorer</span>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Timeline Section */}
      <section ref={containerRef} className="relative py-20 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Ligne centrale animée */}
          <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-slate-700 -translate-x-1/2">
            <motion.div
              style={{ height: lineHeight }}
              className="w-full bg-gradient-to-b from-yellow-400 via-orange-500 to-red-500"
            />
          </div>

          {/* Timeline items */}
          <div className="relative space-y-24">
            {timelineData.map((item, index) => (
              <TimelineCard
                key={index}
                item={item}
                index={index}
                isActive={activeIndex === index}
                onClick={() => {
                  setActiveIndex(index);
                  setSelectedItem(item);
                }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Section Vidéos YouTube */}
      <section className="py-20 px-4 bg-slate-900">
        <div className="max-w-6xl mx-auto">
          {/* Header de la section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-600 rounded-2xl mb-6">
              <Youtube className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
              Nos Mini-Pelles{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">
                en Vidéo
              </span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Découvrez nos machines en action grâce aux vidéos officielles LiuGong. 
              Performances, démonstrations et tests terrain.
            </p>
          </motion.div>

          {/* Stats vidéos */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-wrap justify-center gap-4 mb-12"
          >
            <div className="bg-slate-800 px-6 py-3 rounded-full">
              <span className="text-yellow-400 font-bold">{videoData.length}</span>
              <span className="text-slate-400 ml-2">Modèles</span>
            </div>
            <div className="bg-slate-800 px-6 py-3 rounded-full">
              <span className="text-yellow-400 font-bold">
                {videoData.reduce((acc, m) => acc + m.videos.length, 0)}
              </span>
              <span className="text-slate-400 ml-2">Vidéos</span>
            </div>
            <div className="bg-slate-800 px-6 py-3 rounded-full">
              <span className="text-yellow-400 font-bold">🇫🇷 🇬🇧</span>
              <span className="text-slate-400 ml-2">FR & EN</span>
            </div>
          </motion.div>

          {/* Liste des modèles avec vidéos */}
          <div className="space-y-4">
            {videoData.map((model, index) => (
              <ModelSection key={model.model} model={model} index={index} />
            ))}
          </div>

          {/* Lien vers chaîne YouTube */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <a
              href="https://www.youtube.com/@LiuGongMachinery"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-all hover:shadow-lg hover:shadow-red-600/30"
            >
              <Youtube className="w-6 h-6" />
              Voir toutes les vidéos sur YouTube
              <ChevronRight className="w-5 h-5" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-20 px-4">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center"
        >
          <div className="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-3xl p-12 shadow-2xl shadow-yellow-500/20">
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
              Prêt à écrire l'histoire avec nous ?
            </h2>
            <p className="text-white/90 text-lg mb-8">
              Découvrez notre gamme de mini-pelles et équipements LiuGong disponibles à La Réunion
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/portail/machines">
                <Button className="bg-white text-orange-600 hover:bg-slate-100 px-8 py-3 rounded-full font-semibold text-lg">
                  Voir nos machines
                </Button>
              </Link>
              <Link to="/portail/devis">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-3 rounded-full font-semibold text-lg">
                  Demander un devis
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Modal de détail */}
      {selectedItem && (
        <DetailModal item={selectedItem} onClose={() => setSelectedItem(null)} />
      )}
    </div>
  );
}
